import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Menu
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import { useSidebar } from '../../../../context/SidebarContext';
import apiCall from '../../../../utils/api';
import { useCallback } from 'react';
import AlertPopup from '../../../../components/common/AlertPopup';
import ConfirmationDialog from '../../../../components/common/ConfirmationDialog';
import ActionPopup from '../../../../components/common/ActionPopup';
import StaffDetailDrawer from '../../../Organization/StaffManagement/components/StaffDetailDrawer';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  CreateButton
} from '../../styles';

const OrganizationStaffListPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();

  const [staff, setStaff] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    roleId: ''
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = useState<any | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailUserMapping, setDetailUserMapping] = useState<any | null>(null);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    userId: number;
    mappingId: number;
  }>({ open: false, userId: 0, mappingId: 0 });

  const [actionPopup, setActionPopup] = useState<{
    open: boolean;
    userId: number;
    mappingId: number;
    action: 'deactivate' | 'suspend';
    title: string;
    message: string;
  }>({ open: false, userId: 0, mappingId: 0, action: 'deactivate', title: '', message: '' });

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, mapping: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuUser(mapping);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUser(null);
  };

  const handleRowClick = (mapping: any) => {
    setDetailUserMapping(mapping);
    setDetailDrawerOpen(true);
  };

  const handleStatusClick = (userId: number, mappingId: number, action: 'activate' | 'deactivate' | 'suspend') => {
    handleMenuClose();
    if (action === 'activate') {
      setConfirmDialog({ open: true, userId, mappingId });
    } else {
      const actionLabels: Record<string, string> = { deactivate: 'Deactivate', suspend: 'Suspend' };
      setActionPopup({
        open: true,
        userId,
        mappingId,
        action,
        title: `${actionLabels[action]} Staff Member`,
        message: `Are you sure you want to ${action} this staff member?`,
      });
    }
  };

  const executeStatusUpdate = async (mappingId: number, action: string, remark?: string) => {
    try {
      await apiCall('patch', `/api/organization/superadmin/staff/status/${mappingId}/`, {
        action,
        remark
      });
      setToast({ open: true, message: `Staff member ${action}d successfully`, severity: 'success' });
      fetchStaff();
    } catch (error: any) {
      console.error('Error updating staff status:', error);
      setToast({ open: true, message: error.response?.data?.message || `Failed to ${action} staff member`, severity: 'error' });
    }
  };

  const handleConfirmActivate = () => {
    executeStatusUpdate(confirmDialog.mappingId, 'activate');
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const handleActionSubmit = (remark?: string) => {
    executeStatusUpdate(actionPopup.mappingId, actionPopup.action, remark);
    setActionPopup({ ...actionPopup, open: false });
  };

  const fetchStaff = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiCall<any>('get', `/api/organization/superadmin/staff/${id}/`);
      if (response.data) {
        setStaff(response.data.data || response.data || []);
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
      setToast({ open: true, message: 'Failed to load staff list', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const fetchRoles = useCallback(async () => {
    try {
      const response = await apiCall<any>('get', `/api/organization/superadmin/roles/${id}/`);
      if (response.data) {
        setRoles(response.data.data || response.data || []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  }, [id]);

  useEffect(() => {
    setActivePage('organizations');
    setPath([
      { title: 'Super Admin', path: '/super-admin' },
      { title: 'Organizations', path: '/super-admin/organizations' },
      { title: 'Overview', path: `/super-admin/organizations/${id}` },
      { title: 'Staff List', path: `/super-admin/organizations/${id}/staff` },
    ]);
    setIsShow(true);
    fetchStaff();
    fetchRoles();
  }, [id, setActivePage, setPath, setIsShow, fetchStaff, fetchRoles]);

  const handleDeleteStaff = async (mappingId: number) => {
    if (window.confirm('Are you sure you want to delete this staff member?')) {
      try {
        await apiCall('delete', `/api/organization/superadmin/staff/delete/${mappingId}/`);
        setToast({ open: true, message: 'Staff removed successfully', severity: 'success' });
        fetchStaff();
      } catch (error) {
        console.error('Error deleting staff:', error);
        setToast({ open: true, message: 'Failed to delete staff member', severity: 'error' });
      }
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setFormError(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      roleId: ''
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: any) => {
    setFormData(prev => ({ ...prev, roleId: e.target.value }));
  };

  const handleAddStaff = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.roleId) {
      setFormError('All fields are required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormError('Invalid email format');
      return;
    }

    try {
      setIsSubmitting(true);
      setFormError(null);
      await apiCall('post', '/api/organization/superadmin/staff/create/', {
        org_id: id,
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone_number: formData.phoneNumber,
        role_id: formData.roleId
      });
      
      setToast({ open: true, message: 'Staff member added successfully', severity: 'success' });
      handleCloseModal();
      fetchStaff();
    } catch (error: any) {
      console.error('Error adding staff:', error);
      const errorMessage = error.response?.data?.message || 'Failed to add staff member';
      setFormError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <MainContainer>
        <HeaderContainer>
          <PageTitle>Organization Staff List</PageTitle>
          <CreateButton variant="contained" disableElevation onClick={handleOpenModal}>
            Add New Staff
          </CreateButton>
        </HeaderContainer>

        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee', borderRadius: '8px' }}>
          <Table sx={{ minWidth: 650 }}>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>ID</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Email</StyledTableCell>
                <StyledTableCell>Role</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell align="right">Actions</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {staff.length > 0 ? (
                staff.map((member) => (
                  <StyledTableRow 
                    key={member.mapping_id}
                    onClick={() => handleRowClick(member)}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#F9FAF9 !important' } }}
                  >
                    <StyledTableCell>{member.mapping_id}</StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 600 }}>
                      {member.user?.first_name} {member.user?.last_name}
                    </StyledTableCell>
                    <StyledTableCell>{member.user?.email}</StyledTableCell>
                    <StyledTableCell>
                      <Chip 
                        label={member.system_role?.role_name || 'No Role'} 
                        size="small" 
                        sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontWeight: 500 }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      {(() => {
                        if (member.user?.is_suspended) {
                          return (
                            <Chip 
                              label="Suspended" 
                              size="small" 
                              color="error"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          );
                        }
                        if (!member.user?.is_active) {
                          return (
                            <Chip 
                              label="Deactivated" 
                              size="small" 
                              color="default"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          );
                        }
                        return (
                          <Chip 
                            label="Active" 
                            size="small" 
                            color="success"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        );
                      })()}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <Tooltip title="Actions">
                        <IconButton 
                          onClick={(e) => handleMenuOpen(e, member)}
                          sx={{ color: '#666' }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    {isLoading ? <CircularProgress size={24} /> : 'No staff members found.'}
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: '#333' }}>Add New Organization Staff</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {formError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {formError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
                <TextField
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              </Box>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                error={!!formError && formError.includes('email')}
              />
              <TextField
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
              />
              <FormControl fullWidth required>
                <InputLabel>System Role</InputLabel>
                <Select
                  value={formData.roleId}
                  label="System Role"
                  onChange={handleSelectChange}
                >
                  {roles.map((role) => (
                    <MenuItem key={role.role_id} value={role.role_id}>
                      {role.role_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, px: 3 }}>
            <Button onClick={handleCloseModal} sx={{ color: '#666' }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddStaff} 
              variant="contained" 
              disabled={isSubmitting}
              sx={{ 
                backgroundColor: '#5E8E7E', 
                '&:hover': { backgroundColor: '#4a7063' }
              }}
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Create Staff'}
            </Button>
          </DialogActions>
        </Dialog>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 2,
            sx: { minWidth: 150, mt: 1 }
          }}
        >
          {menuUser && menuUser.user?.is_active ? (
            <MenuItem onClick={(e) => { e.stopPropagation(); handleStatusClick(menuUser.user.id, menuUser.mapping_id, 'deactivate'); }}>
              Deactivate User
            </MenuItem>
          ) : (
            <MenuItem onClick={(e) => { e.stopPropagation(); handleStatusClick(menuUser?.user?.id as number, menuUser?.mapping_id, 'activate'); }}>
              Activate User
            </MenuItem>
          )}
          {menuUser && !menuUser.user?.is_suspended && (
            <MenuItem onClick={(e) => { e.stopPropagation(); handleStatusClick(menuUser.user.id, menuUser.mapping_id, 'suspend'); }} sx={{ color: 'warning.main' }}>
              Suspend User
            </MenuItem>
          )}
          <MenuItem 
            onClick={(e) => { 
              e.stopPropagation(); 
              handleMenuClose(); 
              if (menuUser?.mapping_id) handleDeleteStaff(menuUser.mapping_id); 
            }} 
            sx={{ color: 'error.main' }}
          >
            Delete User
          </MenuItem>
        </Menu>

        <ConfirmationDialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
          onConfirm={handleConfirmActivate}
          title="Activate Staff Member"
          message="Are you sure you want to activate this staff member?"
        />

        <ActionPopup
          open={actionPopup.open}
          onClose={() => setActionPopup({ ...actionPopup, open: false })}
          onSubmit={handleActionSubmit}
          title={actionPopup.title}
          message={actionPopup.message}
          submitButtonText="Confirm"
          textFieldLabel="Reason"
          textFieldPlaceholder="Please provide a reason..."
          required
        />

        <StaffDetailDrawer
          open={detailDrawerOpen}
          onClose={() => setDetailDrawerOpen(false)}
          mapping={detailUserMapping}
        />

        <AlertPopup
          open={toast.open}
          message={toast.message}
          severity={toast.severity}
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
        />
      </MainContainer>
    </Box>
  );
};

export default OrganizationStaffListPage;
